import { isNumber } from 'lodash'

import type { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'
import { CatalogAttribute, Filter } from '@marketplace-web/catalog/catalog-data'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { ListItemContentType } from 'constants/tracking/content-types'
import { Screen } from 'constants/tracking/screens'

import { BuyButtonPosition } from '@marketplace-web/messaging/conversation-messaging-feature'
import {
  CreditCardAddSdkType,
  CreditCardFormLocation,
  CreditCardType,
  PayInMethodCode,
  PaymentsProvider,
} from '@marketplace-web/checkout-payments/checkout-payments-options-data'
import {
  EscrowOrderDetailsClickTarget,
  EscrowOrderStatus,
} from '@marketplace-web/order-details/order-details-data'
import {
  RegistrationBehaviourAction,
  RegistrationBehaviourActionDetails,
  RegistrationType,
} from '@marketplace-web/domain/identity'

export type clickEventArgs = {
  target: ClickableElement
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: ClickableElement
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: clickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type kycClickEventArgs = {
  target: ClickableElement
  details?: string | number | null
}

export const kycClickEvent = (args: kycClickEventArgs): TrackingEvent => {
  const extra: kycClickEventArgs = {
    target: args.target,
    ...(args.details ? { details: args.details } : {}),
  }

  return {
    event: 'kyc.user.click',
    extra,
  }
}

type FavouriteItemEventArgs = {
  itemId: number
  isFollowEvent: boolean
  contentSource?: ContentSource | null
  searchCorrelationId?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  itemOwnerId?: string | number | null
}

type FavouriteItemEventExtra = {
  item_id: number
  content_source?: ContentSource
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string
  item_owner_id?: string | number
}

export const favouriteItemEvent = ({
  itemId,
  isFollowEvent,
  contentSource,
  searchCorrelationId,
  searchSessionId,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  homepageSessionId,
  itemOwnerId,
}: FavouriteItemEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.favorite_item' : 'user.unfavorite_item'
  const extra: FavouriteItemEventExtra = { item_id: itemId }

  if (contentSource) extra.content_source = contentSource
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (itemOwnerId) extra.item_owner_id = itemOwnerId

  return {
    event: eventName,
    extra,
  }
}

type followUserEventArgs = {
  userId: number
  isFollowEvent: boolean
  contentSource?: ContentSource
}

type FollowUserEventExtra = {
  user_id: number
  content_source?: ContentSource
}

export const followUserEvent = ({
  userId,
  isFollowEvent,
  contentSource,
}: followUserEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.follow_user' : 'user.unfollow_user'

  const extra: FollowUserEventExtra = {
    user_id: userId,
  }

  if (isFollowEvent) extra.content_source = contentSource

  return {
    event: eventName,
    extra,
  }
}

type followBrandEventArgs = {
  brandId: number
  isFollowEvent: boolean
}

type FollowBrandEventExtra = {
  brand_id: number
}

export const followBrandEvent = ({
  brandId,
  isFollowEvent,
}: followBrandEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.follow_brand' : 'user.unfollow_brand'

  const extra: FollowBrandEventExtra = {
    brand_id: brandId,
  }

  return {
    event: eventName,
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

export type ClickListItemEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  searchScore?: string
  searchSignals?: Array<string> | null
  itemDistance?: number | null
  homepageSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ClickListEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  search_score?: string
  search_signals?: Array<string>
  buyer_item_distance_km?: number
  homepage_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const clickListItemEvent = (args: ClickListItemEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    position,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchScore,
    searchSignals,
    itemDistance,
    homepageSessionId,
    screen,
    metadata,
  } = args

  const extra: ClickListEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (searchScore) extra.search_score = searchScore
  if (searchSignals) extra.search_signals = searchSignals
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'user.click_list_item',
    extra,
  }
}

type profileClickEventArgs = {
  target: ClickableElement
  ownerId: string
  screen?: Screen
  itemId?: string
}

type profileClickEventExtra = {
  target: ClickableElement
  owner_id: string
  screen?: Screen
  item_id?: string
}

export const profileClickEvent = (args: profileClickEventArgs): TrackingEvent => {
  const { target, ownerId, screen, itemId } = args

  const extra: profileClickEventExtra = { target, owner_id: ownerId }

  if (screen) extra.screen = screen
  if (itemId) extra.item_id = itemId

  return {
    event: 'profile.click',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/user/view.avdl#L25
type ViewEventTarget =
  | 'facebook_like_button'
  | 'my_items_grid'
  | 'my_items_list'
  | 'my_favorites_grid'
  | 'my_favorites_list'
  | 'catalog_grid'
  | 'catalog_list'
  | 'nps_survey'
  | 'sell_tab_survey'
  | 'seller_after_upload_instructions'
  | 'seller_after_sale_instructions'
  | 'seller_after_transaction_complete_instructions'
  | 'email_confirmation'
  | 'email_confirmed'
  | 'video'
  | 'seller_banner'
  | 'sell_tab_banner'
  | 'seller_funnel_welcome_slide'
  | 'wallet_setup_success'
  | 'wallet_pay_in_success'
  | 'newsletter_subscription_banner'
  | 'editorial_campaign_banner'
  | 'cta_prompt'
  | 'feed_personalization_banner'
  | 'personalization_banner_after_feed'
  | 'ad_box'
  | 'bump_promotion'
  | 'onboarding_modal_card'
  | 'app_rating_dialog'
  | 'upload_onboarding_carousel_card'
  | 'notification'
  | 'see_whole_closet_cta'
  | 'cookie_and_ad_personalization_banner'
  | 'ad_personalization_toggle'
  | 'shipping_price'
  | 'translation_hint'
  | 'listing_increase_banner'
  | 'taxpayers_report'
  | 'taxpayers_tax_rules'
  | 'taxpayers_dac7_banner'
  | 'taxpayers_balance_block_modal'
  | 'taxpayers_sales_block_modal'
  | 'taxpayers_tax_rules_testimonial'
  | 'local_search_suggestion'
  | 'brand_cluster_section'
  | 'photo_picker_info_banner'
  | 'crm_message_video'
  | 'free_return_label_modal'
  | 'catalog_popular_catalog'
  | 'label_type_selection'
  | 'return_order'
  | 'ship_fast_badge_description'
  | 'video_story_feed'
  | 'video_story'
  | 'video_story_upload'
  | 'verify'
  | 'didnt_receive_email'
  | 'start'
  | 'free_bump_top_card'
  | 'free_bump_bottom_banner'
  | 'free_bump_success_message'
  | 'physical_auth_buyer'
  | 'physical_auth_seller'
  | 'favorite_button'
  | 'shipping_discount_details'
  | 'physical_auth_eligible'
  | 'recommended_items_grid'
  | 'shop_bundle'
  | 'measurements_info'
  | 'value_select_modal'
  | 'crm_in_app_message_carousel_slide'
  | 'onboarding_video'
  | 'drop_off_point'
  | 'pricing_info_note'
  | 'taxpayers_dac7_special_verification_banner'
  | 'see_drop_off_points'
  | 'message_action'
  | 'bundle_discount_settings'
  | 'taxpayers_special_verification_balance_block_modal'
  | 'sca_start_modal'
  | 'extend_shipping_deadline'
  | 'verification_completed'
  | 'cp_reminder'
  | 'select_parcel_size'
  | 'user_last_seen'
  | 'seller_feedbacks'
  | 'feedback'
  | 'social_proof_banner'
  | 'promotional_listing_banner'
  | 'electronics_verification_buyer'
  | 'electronics_verification_eligible'
  | 'electronics_verification_seller'
  | 'view_all_user_items_card_cta'
  | 'more_similar_items_card_cta'
  | 'photo_tips'
  | 'username_change_disabled'
  | 'carrier_disable_feedback'
  | 'bump_precheckout_review_order_action'
  | 'get_instant_mobile_notifications_banner'
  | 'seller_insights_blocks'
  | 'campaign_landing_page'
  | 'homepage_icon_modal'
  | 'mv_visibility_banner'

export enum customEventName {
  inboxNotificationImpression = 'inbox_notification_impression',
}

type customEventArgs = {
  eventName: customEventName
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type viewEventArgs = {
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewEventTarget
  target_details?: string
  screen?: string
}

export const customEvent = (args: customEventArgs): TrackingEvent => {
  const { eventName, target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: eventName,
    extra,
  }
}

export const viewEvent = (args: viewEventArgs): TrackingEvent => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type viewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: viewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type kycViewScreenEventArgs = {
  screen: string
  details?: string | number | null
}

export const kycViewScreenEvent = (args: kycViewScreenEventArgs): TrackingEvent => {
  const extra: kycViewScreenEventArgs = {
    screen: args.screen,
    ...(args.details ? { details: args.details } : {}),
  }

  return {
    event: 'kyc.user.view_screen',
    extra,
  }
}

type ClickFilterEventArgs = {
  filter: Filter
  screen?: Screen
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  type?: string
  code?: string
}

type ClickFilterEventExtra = {
  filter: Filter
  screen?: Screen
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
  type?: string
  code?: string
}

export const clickFilterEvent = (args: ClickFilterEventArgs): TrackingEvent => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    type,
    code,
  } = args
  const extra: ClickFilterEventExtra = {
    filter,
    screen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (type) extra.type = type
  if (code) extra.code = code

  return {
    event: 'user.click_filter',
    extra,
  }
}

type checkoutBuyerInputEventArgs = {
  transactionId?: number | null
  targetDetails?: string
  screen: string
  target: string
  valid: boolean | null
}

type CheckoutBuyerInputEventExtra = {
  valid: boolean | null
  screen: string
  target: string
  target_details?: string
  transaction_id?: number
}

export const checkoutBuyerInputEvent = (args: checkoutBuyerInputEventArgs): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: CheckoutBuyerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'checkout.buyer.input',
    extra,
  }
}

type SingleCheckoutBuyerInputEventArgs = {
  valid: boolean | null
  screen: string
  target: string
  checkoutId?: string
  location?: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

type SingleCheckoutBuyerInputEventExtra = {
  valid: boolean | null
  screen: string
  target: string
  checkout_id?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  displayMode: string
}

export const singleCheckoutBuyerInputEvent = (
  args: SingleCheckoutBuyerInputEventArgs,
): TrackingEvent => {
  const { checkoutId, screen, target, valid, location } = args

  const extra: SingleCheckoutBuyerInputEventExtra = {
    screen,
    target,
    valid,
    location: location || CreditCardFormLocation.Checkout,
    displayMode: 'full_screen',
  }

  if (checkoutId) extra.checkout_id = checkoutId

  return {
    event: 'credit_card_add.input',
    extra,
  }
}

type CreditCardAddModalLoadDurationEventArgs = {
  checkoutId: string
  duration: number
}

type CreditCardAddModalLoadDurationEventExtra = {
  checkout_id: string
  duration: number
  location: string
  action: string
  displayMode: string
}

export const creditCardAddModalLoadDurationEvent = (
  args: CreditCardAddModalLoadDurationEventArgs,
): TrackingEvent => {
  const { checkoutId, duration } = args

  // checkoutId must be string, because hooks calling creditCardAddModalLoadDurationEvent() cannot call with undefined.
  const extra: CreditCardAddModalLoadDurationEventExtra = {
    checkout_id: checkoutId,
    location: checkoutId === '' ? CreditCardFormLocation.Settings : CreditCardFormLocation.Checkout,
    action: 'screen_load_duration_in_ms',
    duration,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.interaction',
    extra,
  }
}

type CreditCardAddModalTaskDurationEventArgs = {
  checkoutId?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type CreditCardAddModalTaskDurationEventExtra = {
  checkout_id: string
  duration: number
  location: string
  action: string
  displayMode: string
  action_details: string
}

export const creditCardAddModalTaskDurationEvent = (
  args: CreditCardAddModalTaskDurationEventArgs,
) => {
  const { checkoutId, duration, utc_time_to, utc_time_from } = args

  // checkoutId can be empty '', because it is not available in /settings/payments page.
  const extra: CreditCardAddModalTaskDurationEventExtra = {
    checkout_id: checkoutId || '',
    location: checkoutId ? CreditCardFormLocation.Checkout : CreditCardFormLocation.Settings,
    displayMode: 'full_screen',
    action: 'time_on_task_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'credit_card_add.interaction',
    extra,
  }
}

type transactionSellerInputEventArgs = {
  screen: string
  target: string
  valid: boolean | null
  targetDetails?: string
  transactionId?: number | null
}

type transactionSellerInputEventExtra = {
  screen: string
  target: string
  valid: boolean | null
  target_details?: string
  transaction_id?: number
}

export const transactionSellerInputEvent = (
  args: transactionSellerInputEventArgs,
): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: transactionSellerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'transaction.seller_input',
    extra,
  }
}

type UserInputEventArgs = {
  screen?: Screen
  target: string
  value?: string | null
  state: 'focus' | 'unfocus'
  tempUuid?: string
  isDynamic?: boolean
}

export const userInputEvent = (args: UserInputEventArgs): TrackingEvent => {
  const { state, target, value, screen, tempUuid, isDynamic } = args
  const extra = {
    screen,
    target,
    value,
    state,
    upload_session_id: tempUuid,
    is_dynamic: isDynamic || false,
  }

  return {
    event: 'user.input',
    extra,
  }
}

type ClickBundleDiscountSuggestionEventExtra = {
  discounts_enabled: boolean
  screen: Screen
}

type ClickBundleDiscountSuggestionEventArgs = {
  discountsEnabled: boolean
  screen: Screen
}

export const clickBundleDiscountSuggestionEvent = (
  args: ClickBundleDiscountSuggestionEventArgs,
): TrackingEvent => {
  const { discountsEnabled, screen } = args

  const extra: ClickBundleDiscountSuggestionEventExtra = {
    discounts_enabled: discountsEnabled,
    screen,
  }

  return {
    event: 'user.bundle_discount_settings_click',
    extra,
  }
}

type ClickBundleDiscountEnableEventExtra = {
  enable_discount: boolean
  screen: Screen
}

type ClickBundleDiscountEnableEventArgs = {
  enableDiscount: boolean
  screen: Screen
}

export const clickBundleDiscountEnableEvent = (
  args: ClickBundleDiscountEnableEventArgs,
): TrackingEvent => {
  const { enableDiscount, screen } = args

  const extra: ClickBundleDiscountEnableEventExtra = {
    enable_discount: enableDiscount,
    screen,
  }

  return {
    event: 'user.bundle_discount_enable',
    extra,
  }
}

type CloseBundleDiscountScreenEventExtra = {
  discount_2_items: number | null
  discount_3_items: number | null
  discount_5_items: number | null
  screen: Screen
}

type CloseBundleDiscountScreenEventArgs = {
  discount2Items: number | null
  discount3Items: number | null
  discount5Items: number | null
  screen: Screen
}

export const closeBundleDiscountScreenEvent = (
  args: CloseBundleDiscountScreenEventArgs,
): TrackingEvent => {
  const { discount2Items, discount3Items, discount5Items, screen } = args

  const extra: CloseBundleDiscountScreenEventExtra = {
    discount_2_items: discount2Items,
    discount_3_items: discount3Items,
    discount_5_items: discount5Items,
    screen,
  }

  return {
    event: 'user.bundle_discount_amount',
    extra,
  }
}

type ClickRefundPolicyLinkEventArgs = {
  checkoutId: string
  target: ClickableElement
  screen: Screen
}

export const clickRefundPolicyLinkEvent = (args: ClickRefundPolicyLinkEventArgs): TrackingEvent => {
  const { checkoutId, screen, target } = args

  return {
    event: 'checkout.click',
    extra: {
      checkout_id: checkoutId,
      screen,
      target,
    },
  }
}

type ViewPaymentOptionsScreenEventArgs = {
  checkoutId: string
}

export const viewPaymentOptionsEvent = (args: ViewPaymentOptionsScreenEventArgs): TrackingEvent => {
  return {
    event: 'payment_options.screen',
    extra: {
      checkout_id: args.checkoutId,
      displayMode: null,
    },
  }
}

type ClickPaymentOptionsEventArgs = {
  action?: string
  checkoutId: string
  target: ClickableElement
  paymentMethodCode: PayInMethodCode
}

type ClickPaymentOptionsEventExtra = {
  action?: string
  checkout_id: string
  payment_method: string
  target: ClickableElement
  displayMode: null
}

export const clickPaymentOptionsEvent = (args: ClickPaymentOptionsEventArgs): TrackingEvent => {
  const { action, target, checkoutId, paymentMethodCode } = args

  const extra: ClickPaymentOptionsEventExtra = {
    target,
    checkout_id: checkoutId,
    displayMode: null,
    payment_method: paymentMethodCode?.toLowerCase() || '',
  }

  if (action) extra.action = action

  return {
    event: 'payment_options.click',
    extra,
  }
}

type ViewCreditCardAddEventArgs = {
  screen?: string
  checkoutId?: string
  location?: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

export const viewCreditCardAddEvent = (args: ViewCreditCardAddEventArgs): TrackingEvent => {
  return {
    event: 'credit_card_add.screen',
    extra: {
      screen: args.screen || Screen.CreditCardAdd,
      checkout_id: args.checkoutId || null,
      location: args.location || CreditCardFormLocation.Checkout,
      displayMode: 'full_screen',
    },
  }
}

type CreditCardAddClickEventArgs = {
  cardBrandIdentificator?: string | null
  valid?: boolean
  cardDetailsStored?: boolean
  cardBrand?: CreditCardType
  checkoutId?: string
  target: ClickableElement
  screen?: Screen
  location?: CreditCardFormLocation
  clickId?: string
}

type CreditCardAddClickEventExtra = {
  card_brand_identificator?: string | null
  target: ClickableElement
  valid?: boolean
  card_details_stored?: boolean
  card_brand?: CreditCardType
  checkout_id?: string
  screen: Screen
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  click_id?: string
  displayMode: string
}

export const creditCardAddClickEvent = (args: CreditCardAddClickEventArgs) => {
  const {
    valid,
    cardDetailsStored,
    cardBrand,
    cardBrandIdentificator,
    checkoutId,
    target,
    clickId,
    location,
  } = args

  const extra: CreditCardAddClickEventExtra = {
    screen: args.screen || Screen.CreditCardAdd,
    location: location || CreditCardFormLocation.Checkout,
    card_details_stored: cardDetailsStored,
    checkout_id: checkoutId,
    target,
    valid,
    displayMode: 'full_screen',
  }

  if (clickId) extra.click_id = clickId
  if (cardBrand) extra.card_brand = cardBrand
  if (cardBrandIdentificator) extra.card_brand_identificator = cardBrandIdentificator

  return {
    event: 'credit_card_add.click',
    extra,
  }
}

type SelectCatalogEventArgs = {
  catalogIds: Array<number>
  attributeId?: CatalogAttribute
  screen?: Screen
}

type SelectCatalogEventExtra = {
  catalog_id: string
  attribute_id: number | null
  screen?: string
}

export const selectCatalogEvent = (args: SelectCatalogEventArgs): TrackingEvent => {
  const { catalogIds, attributeId, screen } = args

  const extra: SelectCatalogEventExtra = {
    catalog_id: catalogIds.join(','),
    attribute_id: attributeId ?? null,
  }

  if (screen) extra.screen = screen

  return {
    event: 'user.select_catalog',
    extra,
  }
}

type selfServiceViewScreenEventArgs = {
  screen: string
  transactionId: number | string
  faqEntryId?: number | string | null
  details?: string | null
  variant?: string | null
}

type selfServiceViewScreenEventExtra = {
  screen: string
  transaction_id: number | string
  faq_entry_id?: number | string | null
  details?: string | null
  // "variant" is not present in the schema, however it is used in some places
  variant?: string | null
}

export const selfServiceViewScreenEvent = (args: selfServiceViewScreenEventArgs): TrackingEvent => {
  const { screen, transactionId, faqEntryId, details, variant } = args

  const extra: selfServiceViewScreenEventExtra = {
    screen,
    transaction_id: transactionId,
  }

  if (faqEntryId) extra.faq_entry_id = faqEntryId
  if (details) extra.details = details
  if (variant) extra.variant = variant

  return {
    event: 'user.self_service.view_screen',
    extra,
  }
}

type SettingsUserInputEventArgs = {
  screen: string
  target: string
  isValid: boolean
  targetDetails?: string
}

type SettingsUserInputEventExtra = {
  screen: string
  target: string
  valid: boolean
  target_details?: string
}

export const settingsUserInputEvent = (args: SettingsUserInputEventArgs): TrackingEvent => {
  const { screen, target, isValid, targetDetails } = args

  const extra: SettingsUserInputEventExtra = {
    screen,
    target,
    valid: isValid,
    target_details: targetDetails,
  }

  return {
    event: 'settings.user.input',
    extra,
  }
}

type DonationsClickEventArgs = {
  target: ClickableElement
  screen?: Screen | null
  targetDetails?: string | number
}

type DonationsClickEventExtra = {
  target: string
  screen?: string
  target_details?: string | number
}

export const donationsClickEvent = (args: DonationsClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails } = args

  const extra: DonationsClickEventExtra = {
    target,
  }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'user.click_donations',
    extra,
  }
}

type WantItemButtonExtra =
  | 'message'
  | 'buy'
  | 'item_reservation_request'
  | 'make_offer'
  | 'create_bundle'

type BuyerWantItemEventExtra = {
  item_id: number | null
  transaction_id: number | null
  button: WantItemButtonExtra
  global_search_session_id: string | null
  global_catalog_browse_session_id?: string | null
  search_session_id: string | null
  homepage_session_id?: string | null
}

type BuyerWantItemEventArgs = {
  transactionId: number | null
  itemId: number | null
  button: WantItemButtonExtra
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  searchSessionId: string | null
  homepageSessionId?: string | null
}

export const buyerWantItemEvent = ({
  transactionId,
  itemId,
  button,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  searchSessionId,
  homepageSessionId,
}: BuyerWantItemEventArgs): TrackingEvent => {
  const extra: BuyerWantItemEventExtra = {
    button,
    item_id: itemId,
    transaction_id: transactionId,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    search_session_id: searchSessionId,
    homepage_session_id: homepageSessionId,
  }

  return {
    event: 'buyer.want_item',
    extra,
  }
}

type ItemUploadClickEventArgs = {
  screen?: Screen
  target: ClickableElement
  targetDetails?: string | null
  uploadSessionId: string | null
}

type ItemUploadClickEventExtra = {
  screen?: Screen
  target: ClickableElement
  target_details?: string | null
  upload_session_id: string | null
}

export const itemUploadClickEvent = (args: ItemUploadClickEventArgs): TrackingEvent => {
  const { screen, target, targetDetails, uploadSessionId } = args

  const extra: ItemUploadClickEventExtra = {
    target,
    upload_session_id: uploadSessionId,
  }

  if (screen) extra.screen = screen
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'upload_form.user.click',
    extra,
  }
}

type ItemUploadViewScreenEventArgs = {
  screen: Screen
  details?: string
  uploadSessionId: string | null
}

type ItemUploadViewScreenEventExtra = {
  screen: Screen
  details?: string
  upload_session_id: string | null
}

export const itemUploadViewScreenEvent = (args: ItemUploadViewScreenEventArgs): TrackingEvent => {
  const { screen, details, uploadSessionId } = args

  const extra: ItemUploadViewScreenEventExtra = {
    screen,
    upload_session_id: uploadSessionId,
  }

  if (details) extra.details = details

  return {
    event: 'upload_form.user.view_screen',
    extra,
  }
}

type BuyerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type BuyerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

export const buyerViewAddContactDetailsEvent = (args: BuyerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: BuyerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'buyer.view_add_contact_details',
    extra,
  }
}

type SellerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type SellerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

export const sellerViewAddContactDetailsEvent = (args: SellerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: SellerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'seller.view_add_contact_details',
    extra,
  }
}

type BuyerPrecheckoutEventArgs = {
  transactionId: number
}

type BuyerPrecheckoutEventExtra = {
  transaction_id: number
}

export const buyerPrecheckoutEvent = (args: BuyerPrecheckoutEventArgs) => {
  const { transactionId } = args

  const extra: BuyerPrecheckoutEventExtra = {
    transaction_id: transactionId,
  }

  return {
    event: 'buyer.precheckout',
    extra,
  }
}

type BuyerBuyEventArgs = {
  transactionId: number
  screen: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string | null
  sellerOfferId?: number
  buttonPosition?: BuyButtonPosition
}

type BuyerBuyEventExtra = {
  transaction_id: number
  screen: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string | null
  seller_offer_id?: number
  button_position?: BuyButtonPosition
}

export const buyerBuyEvent = (args: BuyerBuyEventArgs) => {
  const {
    transactionId,
    screen,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    sellerOfferId,
    buttonPosition,
  } = args

  const extra: BuyerBuyEventExtra = {
    transaction_id: transactionId,
    screen,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId,
    homepage_session_id: homepageSessionId,
    seller_offer_id: sellerOfferId,
    button_position: buttonPosition,
  }

  return {
    event: 'buyer.buy',
    extra,
  }
}

type OpenOfferScreenEventArgs = {
  isBuyer: boolean
  currentPrice: number
  currencyCode: string
  openedFrom: string
  transactionId: number
}

type OpenOfferScreenEventExtra = {
  current_price: number
  currency: string
  opened_from: string
  transaction_id: number
}

export const openOfferScreenEvent = (args: OpenOfferScreenEventArgs) => {
  const { isBuyer, currentPrice, currencyCode, openedFrom, transactionId } = args

  const extra: OpenOfferScreenEventExtra = {
    current_price: currentPrice,
    currency: currencyCode,
    opened_from: openedFrom,
    transaction_id: transactionId,
  }

  return {
    event: isBuyer ? 'buyer.open_offer_screen' : 'seller.open_offer_screen',
    extra,
  }
}

export enum CheckoutInteractionType {
  Scrolled = 'scrolled',
  ReturnedBack = 'returned_back',
  CoBrandedCard = 'co_branded_card',
  ScreenOnDuration = 'screen_on_duration',
  ScreenLoadDuration = 'screen_load_duration',
}

type CheckoutInteractionEventArgs = {
  screen: string
  orderId?: number
  checkoutId?: number
  transactionId?: number
  action: CheckoutInteractionType
  actionDetails?:
    | { co_branded_card: boolean }
    | { screen_load_duration: number }
    | { duration_in_milliseconds: number }
}

export const checkoutInteractionEvent = (args: CheckoutInteractionEventArgs): TrackingEvent => {
  const { screen, action, actionDetails, transactionId, orderId, checkoutId } = args

  return {
    event: 'buyer.interacts_with_checkout',
    extra: {
      screen,
      action,
      order_id: orderId || null,
      checkout_id: checkoutId || null,
      transaction_id: transactionId || null,
      action_details: actionDetails ? JSON.stringify(actionDetails) : null,
    },
  }
}

type SearchFaqEntriesEventArgs = {
  searchSessionId: string | null
  searchQuery: string
  faqEntryIds: Array<number>
  helpCenterSessionId: string
}

type SearchFaqEntriesEventExtra = {
  search_session_id: string | null
  search_query: string
  faq_entry_ids: Array<number>
  help_center_session_id: string
}

export const searchFaqEntriesEvent = (args: SearchFaqEntriesEventArgs) => {
  const extra: SearchFaqEntriesEventExtra = {
    search_session_id: args.searchSessionId,
    search_query: args.searchQuery,
    faq_entry_ids: args.faqEntryIds,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.search_faq_entries',
    extra,
  }
}

type LeaveFaqEntryEventArgs = {
  faqEntryId: number
  faqAccessChannel: string | null
  initialPageScrollDepth: number
  maxPageScrollDepth: number
  totalViewTime: number
  helpCenterSessionId: string
}

type LeaveFaqEntryEventExtra = {
  faq_entry_id: number
  faq_access_channel: string | null
  initial_page_scroll_depth: number
  max_page_scroll_depth: number
  total_view_time: number
  help_center_session_id: string
}

export const leaveFaqEntryEvent = (args: LeaveFaqEntryEventArgs) => {
  const extra: LeaveFaqEntryEventExtra = {
    faq_entry_id: args.faqEntryId,
    faq_access_channel: args.faqAccessChannel,
    initial_page_scroll_depth: args.initialPageScrollDepth,
    max_page_scroll_depth: args.maxPageScrollDepth,
    total_view_time: args.totalViewTime,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.leave_faq_entry',
    extra,
  }
}

type ViewFaqEntryArgs = {
  faqEntryId: number
  faqEntryChannel: string
  faqAccessChannel: string | null
  screen: string
  helpCenterSessionId: string
}

type ViewFaqEntryExtra = {
  faq_entry_id: number
  faq_access_channel: string | null
  faq_entry_channel: string
  screen: string
  help_center_session_id: string
}

export const viewFaqEntry = (args: ViewFaqEntryArgs) => {
  const extra: ViewFaqEntryExtra = {
    faq_entry_id: args.faqEntryId,
    faq_entry_channel: args.faqEntryChannel,
    faq_access_channel: args.faqAccessChannel,
    screen: args.screen,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.view_faq_entry',
    extra,
  }
}

type userClickFaqRelatedQuestionEventArgs = {
  helpCenterSessionId: string | null
  initialFaqId?: number
  targetFaqId: number
  faqAccessChannel?: string
  faqEntryChannel?: string
}

export const userClickFaqRelatedQuestionEvent = (
  args: userClickFaqRelatedQuestionEventArgs,
): TrackingEvent => {
  const { helpCenterSessionId, initialFaqId, targetFaqId, faqEntryChannel, faqAccessChannel } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
    initial_faq_id: initialFaqId,
    target_faq_id: targetFaqId,
    faq_entry_channel: faqEntryChannel,
    faq_access_channel: faqAccessChannel,
  }

  return {
    event: 'user.click_faq_related_question',
    extra,
  }
}

type userClickRecommendedTopicEventArgs = {
  helpCenterSessionId: string | null
}

export const userClickRecommendedTopicEvent = (
  args: userClickRecommendedTopicEventArgs,
): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
  }

  return {
    event: 'user.click_recommended_topic',
    extra,
  }
}

type userClickHelpCenterSearchResultArgs = {
  helpCenterSessionId: string | null
}

export const userClickHelpCenterSearchResult = (
  args: userClickHelpCenterSearchResultArgs,
): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
  }

  return {
    event: 'user.click_help_center_search_result',
    extra,
  }
}

export enum FaqFeedback {
  Positive = 'positive',
  Negative = 'negative',
}

type submitFaqFeedbackEventArgs = {
  faqId: number
  helpCenterSessionId: string | null
  faqAccessChannel?: string
  faqEntryChannel?: string
  faqFeedback: FaqFeedback
}

export const userSubmitFaqFeedbackEvent = (args: submitFaqFeedbackEventArgs): TrackingEvent => {
  const { faqId, helpCenterSessionId, faqEntryChannel, faqAccessChannel, faqFeedback } = args
  const extra = {
    faq_id: faqId,
    help_center_session_id: helpCenterSessionId,
    faq_entry_channel: faqEntryChannel,
    faq_access_channel: faqAccessChannel,
    faq_feedback: faqFeedback,
  }

  return {
    event: 'user.submit_faq_feedback',
    extra,
  }
}

type userContactSupportEventArgs = {
  helpCenterSessionId: string | null
  oldThreadId?: string | null
  newThreadId?: string | null
}

type userContactSupportEventExtraArgs = {
  old_thread_id?: string
  help_center_session_id: string | null
  new_thread_id?: string
}

export const userContactSupportEvent = (args: userContactSupportEventArgs): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra: userContactSupportEventExtraArgs = {
    help_center_session_id: helpCenterSessionId,
  }

  if (args.newThreadId) {
    extra.new_thread_id = args.newThreadId
  }

  if (args.oldThreadId) {
    extra.old_thread_id = args.oldThreadId
  }

  return {
    event: 'user.contact_support',
    extra,
  }
}

export enum LiveChatBannerViewStatus {
  Available = 'available',
  Unavailable = 'unavailable',
}

type LiveChatEventsBaseArgs = {
  helpCenterSessionId: string | null
}

type LiveChatBannerViewedArgs = LiveChatEventsBaseArgs & {
  status: LiveChatBannerViewStatus
}

type LiveChatBannerViewedEventExtra = {
  live_chat_status: LiveChatBannerViewStatus
  help_center_session_id: string | null
}

export const liveChatBannerViewedEvent = (args: LiveChatBannerViewedArgs) => {
  const extra: LiveChatBannerViewedEventExtra = {
    live_chat_status: args.status,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_banner_viewed',
    extra,
  }
}

type LiveChatBannerClickedEventExtra = {
  help_center_session_id: string | null
}

export const liveChatBannerClickedEvent = (args: LiveChatEventsBaseArgs) => {
  const extra: LiveChatBannerClickedEventExtra = {
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_banner_clicked',
    extra,
  }
}

type LiveChatFormSubmittedEventExtra = {
  help_center_session_id: string | null
}

export const liveChatFormSubmittedEvent = (args: LiveChatEventsBaseArgs) => {
  const extra: LiveChatFormSubmittedEventExtra = {
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_form_submitted',
    extra,
  }
}

type LiveChatFormViewedEventExtra = {
  help_center_session_id: string | null
}

export const liveChatFormViewedEvent = (args: LiveChatEventsBaseArgs) => {
  const extra: LiveChatFormViewedEventExtra = {
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_form_viewed',
    extra,
  }
}

type InboxSupportFeedbackEventsBaseArgs = {
  threadId: string
  helpCenterSessionId: string
}

type InboxActionButtonEventsArgs = InboxSupportFeedbackEventsBaseArgs & {
  supportType: string
}

type InboxSupportFeedbackClosedEventsArgs = InboxSupportFeedbackEventsBaseArgs & {
  rating?: number
}

export const continueConversationClicked = (args: InboxActionButtonEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    support_type: args.supportType,
  }

  return {
    event: 'support_conversation_actions.continue_conversation_clicked',
    extra,
  }
}

export const chatbotFeedbackClosed = (args: InboxSupportFeedbackClosedEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    rating: args.rating,
  }

  return {
    event: 'automated_support_feedback.chatbot_feedback_closed',
    extra,
  }
}

export const autoreplyFeedbackClosed = (args: InboxSupportFeedbackClosedEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    rating: args.rating,
  }

  return {
    event: 'automated_support_feedback.auto_reply_feedback_closed',
    extra,
  }
}

export const chatbotFeedbackV1Viewed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.chatbot_feedback_v1_viewed',
    extra,
  }
}

export const autoreplyFeedbackV1Viewed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.auto_reply_feedback_v1_viewed',
    extra,
  }
}

type PaymentOptionsTimeOnTaskEventArgs = {
  checkoutId: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type PaymentOptionsTimeOnTaskEventExtra = {
  checkout_id: string
  displayMode: null
  action: string
  duration: number
  action_details: string
}

export const paymentOptionsTimeOnTaskEvent = (args: PaymentOptionsTimeOnTaskEventArgs) => {
  const { checkoutId, duration, utc_time_from, utc_time_to } = args

  const extra: PaymentOptionsTimeOnTaskEventExtra = {
    checkout_id: checkoutId,
    action: 'time_on_task_in_ms',
    duration,
    displayMode: null,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'payment_options.interaction',
    extra,
  }
}

type NoticesUserEventArgs = {
  threadId: string
}

type NoticesUserEventExtra = {
  thread_id: string
}

export const noticesUserOpenFaq = (args: NoticesUserEventArgs) => {
  const extra: NoticesUserEventExtra = {
    thread_id: args.threadId,
  }

  return {
    event: 'notices.user.open_faq',
    extra,
  }
}

export const noticesUserOpenContactForm = (args: NoticesUserEventArgs) => {
  const extra: NoticesUserEventExtra = {
    thread_id: args.threadId,
  }

  return {
    event: 'notices.user.open_contact_form',
    extra,
  }
}

export const noticesUserSubmitTicket = (args: NoticesUserEventArgs) => {
  const extra: NoticesUserEventExtra = {
    thread_id: args.threadId,
  }

  return {
    event: 'notices.user.submit_ticket',
    extra,
  }
}

type SingleCheckoutTokenizationOutcomeEventArgs = {
  errorMessage?: string
  checkoutId?: string
  outcome: 'success' | 'failure'
  providerName: PaymentsProvider
  clickId?: string
  location: CreditCardFormLocation
}

export const singleCheckoutTokenizationOutcomeEvent = (
  args: SingleCheckoutTokenizationOutcomeEventArgs,
): TrackingEvent => {
  const { outcome, errorMessage, providerName, checkoutId, clickId } = args

  const extra = {
    outcome,
    error_message: errorMessage,
    provider_name: providerName,
    checkout_id: checkoutId || null,
    click_id: clickId,
    displayMode: 'full_screen',
    location: args.location,
  }

  return {
    event: 'credit_card_add.tokenization_outcome',
    extra,
  }
}

type singleCheckoutCreditCardAddRegistrationEventArgs = {
  clickId: string
  checkoutId?: string
  cardRegistrationId: string | null
  location: CreditCardFormLocation
}

export const singleCheckoutCreditCardAddRegistrationEvent = (
  args: singleCheckoutCreditCardAddRegistrationEventArgs,
): TrackingEvent => {
  const { checkoutId, clickId, cardRegistrationId, location } = args

  return {
    event: 'credit_card_add.registration',
    extra: {
      location,
      click_id: clickId,
      checkout_id: checkoutId || null,
      card_registration_id: cardRegistrationId,
      displayMode: 'full_screen',
    },
  }
}

type LandingPageViewScreenArgs = {
  screen?: string
  referrer?: string
  path?: string
  event?: string
}

type LandingPageViewScreenExtra = {
  screen?: string
  path?: string
  referrer?: string
}

export const ViewLandingPageEvent = (args: LandingPageViewScreenArgs): TrackingEvent => {
  const { screen, path, referrer } = args

  const extra: LandingPageViewScreenExtra = {
    screen,
    path,
    referrer,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type ReferralsOpenLinkEventArgs = {
  referrerId: number
}

type ReferralsOpenLinkEventExtra = {
  referrer_id: number
}

export const referralsOpenLinkEvent = (args: ReferralsOpenLinkEventArgs): TrackingEvent => {
  const { referrerId } = args

  const extra: ReferralsOpenLinkEventExtra = {
    referrer_id: referrerId,
  }

  return {
    event: 'sharing.referrals.open_link',
    extra,
  }
}

type SystemTimingArgs = {
  section: 'largest_contentful_paint' | 'ad_load' | 'van_ad_load' | 'van_ad_asset_load'
  duration: number
  completionState: 'succeeded'
  data?: string
}

type SystemTimingExtra = {
  section: SystemTimingArgs['section']
  /** Duration in millisecond */
  duration: number
  completion_state: 'succeeded'
  data?: string
}

export const systemTimingEvent = (args: SystemTimingArgs) => {
  const { section, duration, completionState, data } = args

  const extra: SystemTimingExtra = { section, duration, completion_state: completionState, data }

  return {
    event: 'system.timing',
    extra,
  }
}

type singleCheckoutCvvInfoEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

type singleCheckoutCvvInfoEventExtra = {
  checkout_id?: string | null
  screen: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  displayMode: string
}

export const singleCheckoutCvvInfoEvent = (args: singleCheckoutCvvInfoEventArgs) => {
  const { checkoutId, location } = args

  const extra: singleCheckoutCvvInfoEventExtra = {
    checkout_id: checkoutId || null,
    screen: Screen.CvvInformation,
    location,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.screen',
    extra,
  }
}

type singleCheckoutCoBrandedCardEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  capabilityDetails: boolean
}

type singleCheckoutCoBrandedCardEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  capability_type: string
  capability_details: boolean
  displayMode: string
}

export const singleCheckoutCoBrandedCardEvent = (args: singleCheckoutCoBrandedCardEventArgs) => {
  const { checkoutId, location, capabilityDetails } = args

  const extra: singleCheckoutCoBrandedCardEventExtra = {
    checkout_id: checkoutId || null,
    capability_details: capabilityDetails,
    capability_type: CheckoutInteractionType.CoBrandedCard,
    location,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.buyer_has_capability',
    extra,
  }
}

type SingleCheckoutCoBrandedCardSelectionClickEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

type SingleCheckoutCoBrandedCardSelectionClickEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  screen: string
  displayMode: string
}

export const singleCheckoutCoBrandedCardSelectionClickEvent = (
  args: SingleCheckoutCoBrandedCardSelectionClickEventArgs,
) => {
  const { checkoutId, location } = args

  const extra: SingleCheckoutCoBrandedCardSelectionClickEventExtra = {
    checkout_id: checkoutId || null,
    screen: Screen.CoBrandedSelection,
    location,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.screen',
    extra,
  }
}

type SingleCheckoutCoBrandedInfoModalFaqEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation
}

type SingleCheckoutCoBrandedInfoModalFaqEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  screen: string
  target: string
  displayMode: string
}

export const singleCheckoutCoBrandedInfoModalFaqEvent = (
  args: SingleCheckoutCoBrandedInfoModalFaqEventArgs,
) => {
  const { checkoutId, location } = args

  const extra: SingleCheckoutCoBrandedInfoModalFaqEventExtra = {
    location,
    checkout_id: checkoutId || null,
    screen: Screen.CoBrandedSelection,
    target: ClickableElement.CobrandedModalInfo,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.click',
    extra,
  }
}

type SingleCheckoutCoBrandedCardSelectedEventArgs = {
  checkoutId?: string
  cardBrand: string
  location: CreditCardFormLocation
}

type SingleCheckoutCoBrandedCardSelectedEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation
  screen: string
  target: string
  card_brand: string
  displayMode: string
}

export const singleCheckoutCoBrandedCardSelectedEvent = (
  args: SingleCheckoutCoBrandedCardSelectedEventArgs,
) => {
  const { checkoutId, cardBrand, location } = args

  const extra: SingleCheckoutCoBrandedCardSelectedEventExtra = {
    location,
    card_brand: cardBrand,
    checkout_id: checkoutId || null,
    screen: Screen.CoBrandedSelection,
    target: ClickableElement.CobrandedModalSelection,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.click',
    extra,
  }
}

type CreditCardAddReturnFromRedirectEventArgs = {
  checkoutId?: string
  location?: CreditCardFormLocation
}

type CreditCardAddReturnFromRedirectEventExtra = {
  checkout_id: string | null
  return_from_type: string
  location: CreditCardFormLocation
}

export const creditCardAddReturnFromRedirectEvent = (
  args: CreditCardAddReturnFromRedirectEventArgs,
) => {
  const { checkoutId, location } = args

  const extra: CreditCardAddReturnFromRedirectEventExtra = {
    checkout_id: checkoutId || null,
    location: location || CreditCardFormLocation.Checkout,
    return_from_type: 'web_view',
  }

  return {
    event: 'credit_card_add.return_from_redirect',
    extra,
  }
}

type CreditCardAddSDKoutcomeEventArgs = {
  checkoutId?: string
  clickId: string | null
  outcome: 'success' | 'failure'
  errorMessage?: string
  provider?: PaymentsProvider
  type: CreditCardAddSdkType
}

type CreditCardAddSDKoutcomeEventExtra = {
  checkout_id: string | null
  click_id: string | null
  outcome: 'success' | 'failure'
  displaymode: string
  type: CreditCardAddSdkType
  provider?: PaymentsProvider
  error_message: string | null
  location: CreditCardFormLocation
}

export const creditCardAddSDKoutcomeEvent = (args: CreditCardAddSDKoutcomeEventArgs) => {
  const { checkoutId, clickId, provider, type, outcome, errorMessage } = args

  const extra: CreditCardAddSDKoutcomeEventExtra = {
    checkout_id: checkoutId || null,
    click_id: clickId,
    type,
    outcome,
    provider,
    displaymode: 'full_screen',
    error_message: errorMessage || null,
    location: checkoutId ? CreditCardFormLocation.Checkout : CreditCardFormLocation.Settings,
  }

  return {
    event: 'credit_card_add.sdk_outcome',
    extra,
  }
}

type CheckoutReturnFromRedirectEventArgs = {
  checkoutId: string
  paymentMethodCode?: string
}

type CheckoutReturnFromRedirectEventExtra = {
  checkout_id: string
  payment_method: string
  return_from_type: string
}

export const checkoutReturnFromRedirectEvent = (args: CheckoutReturnFromRedirectEventArgs) => {
  const { checkoutId, paymentMethodCode } = args

  const extra: CheckoutReturnFromRedirectEventExtra = {
    checkout_id: checkoutId,
    return_from_type: 'web_view',
    payment_method: paymentMethodCode || 'DEFAULT_CREDIT_CARD',
  }

  return {
    event: 'checkout.return_from_redirect',
    extra,
  }
}

type ItemAttributesEventArgs = {
  itemId: string
  attributeCode: string
  attributeValueId?: string
  details?: string
  itemPageSessionId?: string
}

type ItemAttributesEventExtra = {
  screen: string
  item_id: string
  attribute_code: string
  attribute_value_id?: string | null
  details?: string | null
  item_page_session_id?: string | null
}

export const itemAttributesClickEvent = (args: ItemAttributesEventArgs) => {
  const { itemId, attributeCode, attributeValueId, details, itemPageSessionId } = args

  const extra: ItemAttributesEventExtra = {
    screen: Screen.Item,
    item_id: itemId,
    attribute_code: attributeCode,
  }

  if (attributeValueId) extra.attribute_value_id = attributeValueId
  if (details) extra.details = details
  if (itemPageSessionId) extra.item_page_session_id = itemPageSessionId

  return {
    event: 'item_page.item_attributes.click',
    extra,
  }
}

type PartialRefundOffersClickEventArgs = {
  target: ClickableElement
  transactionId: string
  complaintId: string
  targetDetails?: string
}

type PartialRefundOffersClickEventExtra = {
  target: ClickableElement
  transaction_id: string
  complaint_id: string
  target_details?: string
}

export const partialRefundOffersClickEvent = (args: PartialRefundOffersClickEventArgs) => {
  const { target, transactionId, complaintId, targetDetails } = args

  const extra: PartialRefundOffersClickEventExtra = {
    target,
    transaction_id: transactionId,
    complaint_id: complaintId,
  }

  if (targetDetails) extra.target_details = targetDetails

  return { event: 'partial_refund_offers.click', extra }
}

type ItemPageViewEventExtra = {
  target: string
  item_id: string
  details?: string
  item_page_session_id?: string
}

export const itemPageViewEvent = (args: ItemPageViewEventExtra) => {
  const { target, item_id, details, item_page_session_id } = args

  const extra: ItemPageViewEventExtra = {
    target,
    item_id,
  }

  if (details) extra.details = details
  if (item_page_session_id) extra.item_page_session_id = item_page_session_id

  return {
    event: 'item_page.view',
    extra,
  }
}

type ShippingClickEventArgs = {
  screen: string
  checkoutId: string
  target: ClickableElement
  valid?: boolean
  info?: string
  infoPii?: string
}

type ShippingClickEventExtra = {
  screen: string
  checkout_id: string
  target: string
  valid?: boolean
  info?: string
  info_pii?: string
}

export const shippingClickEvent = (args: ShippingClickEventArgs): TrackingEvent => {
  const { screen, checkoutId, valid, target, info, infoPii } = args

  const extra: ShippingClickEventExtra = {
    screen,
    checkout_id: checkoutId,
    valid,
    target,
    info,
    info_pii: infoPii,
  }

  return { event: 'shipping_options.click', extra }
}

type shippingAddressInteractionEventArgs = {
  checkoutId: string
  screen?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type shippingAddressInteractionEventExtra = {
  checkout_id: string
  screen: string
  action: string
  duration: number
  action_details: string
}

export const shippingAddressInteractionEvent = (
  args: shippingAddressInteractionEventArgs,
  isTimeOnTask: boolean,
) => {
  const { checkoutId, screen, duration, utc_time_from, utc_time_to } = args

  const extra: shippingAddressInteractionEventExtra = {
    checkout_id: checkoutId,
    screen: screen || '',
    action: isTimeOnTask ? 'time_on_task_in_ms' : 'screen_load_duration_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'shipping_address.interaction',
    extra,
  }
}

type shippingInteractionEventArgs = {
  checkoutId: string
  screen?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type shippingInteractionEventExtra = {
  checkout_id: string
  screen: string
  action: string
  duration: number
  action_details: string
}

export const shippingInteractionEvent = (
  args: shippingInteractionEventArgs,
  isTimeOnTask: boolean,
) => {
  const { checkoutId, screen, duration, utc_time_from, utc_time_to } = args

  const extra: shippingInteractionEventExtra = {
    checkout_id: checkoutId,
    screen: screen || '',
    action: isTimeOnTask ? 'time_on_task_in_ms' : 'screen_load_duration_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'shipping_options.interaction',
    extra,
  }
}

type shippingContactInteractionEventArgs = {
  checkoutId: string
  screen?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type shippingContactInteractionEventExtra = {
  checkout_id: string
  screen: string
  action: string
  duration: number
  action_details: string
}

export const shippingContactInteractionEvent = (
  args: shippingContactInteractionEventArgs,
  isTimeOnTask: boolean,
) => {
  const { checkoutId, screen, duration, utc_time_from, utc_time_to } = args

  const extra: shippingContactInteractionEventExtra = {
    checkout_id: checkoutId,
    screen: screen || '',
    action: isTimeOnTask ? 'time_on_task_in_ms' : 'screen_load_duration_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'contact_details.interaction',
    extra,
  }
}

type TaxpayersClickEventArgs = {
  screen: string
  target: 'block_modal' | 'banner' | 'hyperlink' | 'button'
  target_name: string
  target_details: string | null
}

export const taxpayersClickEvent = (args: TaxpayersClickEventArgs): TrackingEvent => {
  const { screen, target, target_details, target_name } = args

  return {
    event: 'taxpayers.click',
    extra: {
      screen,
      target,
      target_details,
      target_name,
    },
  }
}

type TaxpayersViewEventArgs = {
  screen: string
  target: 'block_modal' | 'banner'
  target_details: string
}

export const taxpayersViewEvent = (args: TaxpayersViewEventArgs): TrackingEvent => {
  const { screen, target, target_details } = args

  return {
    event: 'taxpayers.view',
    extra: { screen, target, target_details },
  }
}

type EscrowOrderDetailsActionLatencyViewEventArgs = {
  screen: string
  escrow_order_id: string
  escrow_order_status?: EscrowOrderStatus
  actions: Array<string> | null
  latency_ms: string
  details: string | null
}

export const escrowOrderDetailsActionLatencyViewEvent = (
  args: EscrowOrderDetailsActionLatencyViewEventArgs,
): TrackingEvent => {
  const { screen, escrow_order_id, escrow_order_status, actions, latency_ms, details } = args

  return {
    event: 'escrow_order_details.action_latency.view',
    extra: {
      screen,
      escrow_order_id,
      escrow_order_status,
      actions,
      latency_ms,
      details,
    },
  }
}

type EscrowOrderDetailsClickEventArgs = {
  screen: string
  escrow_order_id: string
  user_side: string
  escrow_order_status?: EscrowOrderStatus
  is_suspended?: boolean
  target: EscrowOrderDetailsClickTarget
  details: string | null
}

export const escrowOrderDetailsClickEvent = (
  args: EscrowOrderDetailsClickEventArgs,
): TrackingEvent => {
  const { screen, escrow_order_id, user_side, escrow_order_status, is_suspended, target, details } =
    args

  return {
    event: 'escrow_order_details.click',
    extra: {
      screen,
      escrow_order_id,
      user_side,
      escrow_order_status,
      is_suspended,
      target,
      details,
    },
  }
}

type UserReportSubmittedEventArgs = {
  entity_type: string
  entity_id: string
  reason_id: string
  photo_id: string | null
  message_id: string | null
  report_session_id: string
}

export const userReportSubmittedEvent = (args: UserReportSubmittedEventArgs): TrackingEvent => {
  const { entity_type, entity_id, reason_id, photo_id, message_id, report_session_id } = args

  return {
    event: 'report.user.report_submitted',
    extra: {
      entity_type,
      entity_id,
      reason_id,
      photo_id,
      message_id,
      report_session_id,
    },
  }
}

type UserReportViewScreenEventArgs = {
  screen: string
  report_session_id: string
  entity_id: string
  entity_type: string
  photo_id: string | null
  message_id: string | null
  details: string | null
}

export const userReportViewScreenEvent = (args: UserReportViewScreenEventArgs): TrackingEvent => {
  const { screen, report_session_id, entity_id, entity_type, photo_id, message_id, details } = args

  return {
    event: 'report.user.view_screen',
    extra: {
      screen,
      report_session_id,
      entity_id,
      entity_type,
      photo_id,
      message_id,
      details,
    },
  }
}

type UserNoticeAndActionFormViewScreenEventArgs = {
  sessionId: string
  reporterType: string
}

type UserNoticeAndActionFormViewScreenExtra = {
  session_id: string
  reporter_type: string
}

export const userNoticeAndActionFormViewScreenEvent = (
  args: UserNoticeAndActionFormViewScreenEventArgs,
): TrackingEvent => {
  const { sessionId, reporterType } = args

  const extra: UserNoticeAndActionFormViewScreenExtra = {
    session_id: sessionId,
    reporter_type: reporterType,
  }

  return {
    event: 'notice_and_action_form.user.view_screen',
    extra,
  }
}

type UserNoticeAndActionFormReportSubmittedEventArgs = {
  sessionId: string
  reporterType: string
  emailAddress: string
  brand: string | null
  urlCount: number
}

type UserNoticeAndActionFormReportSubmittedExtra = {
  session_id: string
  reporter_type: string
  email_address: string
  brand: string | null
  url_count: number
}

export const userNoticeAndActionFormReportSubmittedEvent = (
  args: UserNoticeAndActionFormReportSubmittedEventArgs,
): TrackingEvent => {
  const { sessionId, reporterType, emailAddress, brand, urlCount } = args

  const extra: UserNoticeAndActionFormReportSubmittedExtra = {
    session_id: sessionId,
    reporter_type: reporterType,
    email_address: emailAddress,
    brand,
    url_count: urlCount,
  }

  return {
    event: 'notice_and_action_form.user.report_submitted',
    extra,
  }
}

type UserNoticeAndActionFormUrlValidationEventArgs = {
  sessionId: string
  reporterType: string
}

type UserNoticeAndActionFormUrlValidationExtra = {
  session_id: string
  reporter_type: string
}

export const userNoticeAndActionFormUrlValidationEvent = (
  args: UserNoticeAndActionFormUrlValidationEventArgs,
): TrackingEvent => {
  const { sessionId, reporterType } = args

  const extra: UserNoticeAndActionFormUrlValidationExtra = {
    session_id: sessionId,
    reporter_type: reporterType,
  }

  return {
    event: 'notice_and_action_form.user.url_validation',
    extra,
  }
}

type RegistrationFormBehaviourEventArgs = {
  taskId: string
  fingerprint: string
  countryCode: string
  screen: string
  authenticationType: RegistrationType
  action: RegistrationBehaviourAction
  actionDetails?: RegistrationBehaviourActionDetails
  inApp: boolean
  userId?: number
}

export const registrationFormBehaviourEvent = ({
  taskId,
  fingerprint,
  countryCode,
  screen,
  authenticationType,
  action,
  actionDetails,
  inApp,
  userId,
}: RegistrationFormBehaviourEventArgs): TrackingEvent => {
  return {
    event: 'registration.form_behaviour',
    userId,
    extra: {
      task_id: taskId,
      screen,
      authentication_type: authenticationType,
      action,
      in_app: inApp,
      country_code: countryCode,
      fingerprint,
      action_details: actionDetails,
    },
  }
}
